/**
 * API Client for Support System Backend
 */

import type {
  Ticket,
  TicketsResponse,
  ArchivedTicketsResponse,
  ArchivedConversationsResponse,
  TicketResponse,
  ConversationMessagesResponse,
  SuccessResponse,
  Category,
  ServiceType,
  ServiceTypeOption,
  DashboardStats,
  Attachment,
  PickerCategory,
  CannedCategory,
  CannedAnswer,
  CreateCategoryInput,
  UpdateCategoryInput,
  CreateAnswerInput,
  UpdateAnswerInput,
  ImportValidateResult,
  ImportCommitResult,
  Broadcast,
  BroadcastDetail,
  AudienceFilter,
  CreateBroadcastInput,
  UploadSummary,
  CreateFromUploadResult,
} from '../types';

// Default to the page's own origin so a stale `VITE_API_URL` baked into a
// cached bundle can't strand the miniapp on a dead host. The Vite dev server
// proxies /api and /tickets to the worker; in prod the miniapp is served
// same-origin with the API, so this is a no-op there.
const WORKER_URL = import.meta.env.VITE_API_URL || window.location.origin;
const API_BASE_URL = `${WORKER_URL}/api`;
const AUTH_TOKEN_KEY = 'support_auth_token';

interface ApiResponse<T> {
  success: boolean;
  data?: T;
  error?: string;
}

/**
 * Phase 32 inline-divider: external metadata returned by the batch
 * external-refs endpoint. Keyed by ticketId in the response map.
 */
export interface ExternalContext {
  externalReferenceId: string;
  externalSource: string;
  externalTitle: string | null;
  externalDescriptionMd: string | null;
  externalFetchedAt: string;
  // Phase 36 (plan 36-02): structured case context. All OPTIONAL — present
  // only when the server's normalizeCase() parsed fields (omitted on organic/
  // unparseable rows). externalDescriptionMd above remains the fallback.
  externalCaseType?: string;
  externalFields?: { label: string; value: string }[];
  externalReason?: string;
  // SimVerifier initDataDetails passthrough: the partner's person/identity
  // card (parsed JSON). Rendered dynamically behind a "Show user details"
  // toggle. null/absent on organic tickets and external tickets without it.
  externalInitData?: Record<string, unknown> | null;
}

class APIClient {
  private inFlight = new Map<string, Promise<ApiResponse<unknown>>>();
  // Single-flight guard so a burst of simultaneous 401s triggers exactly one
  // re-registration, not one per in-flight request.
  private reauthInFlight: Promise<boolean> | null = null;

  private async dedupe<T>(key: string, fn: () => Promise<ApiResponse<T>>): Promise<ApiResponse<T>> {
    const existing = this.inFlight.get(key);
    if (existing) return (await existing) as ApiResponse<T>;
    const p = fn() as Promise<ApiResponse<unknown>>;
    this.inFlight.set(key, p);
    try {
      return (await p) as ApiResponse<T>;
    } finally {
      this.inFlight.delete(key);
    }
  }
  /**
   * Get auth headers based on available authentication
   */
  private getAuthHeaders(): Record<string, string> {
    const headers: Record<string, string> = {};

    // Check for JWT token
    const token = localStorage.getItem(AUTH_TOKEN_KEY);
    if (token) {
      headers['Authorization'] = `Bearer ${token}`;
    }

    return headers;
  }

  /**
   * Silently re-register using a fresh signed initData blob from the bridge.
   * Used when a request hits 401 (token expired). Returns true if a new token
   * was minted and stored. Single-flight via reauthInFlight.
   *
   * Dynamic import of ./bridge avoids a static import cycle (bridge ↔ api).
   */
  private async reauthenticate(): Promise<boolean> {
    if (this.reauthInFlight) return this.reauthInFlight;
    this.reauthInFlight = (async () => {
      try {
        const { bridgeService } = await import('./bridge');
        const deviceId = await bridgeService.extractDeviceId();
        if (!deviceId) return false;
        const initData = await bridgeService.getRawInitData();
        const res = await this.register(deviceId, initData ?? undefined);
        return res.success === true;
      } catch {
        return false;
      }
    })();
    try {
      return await this.reauthInFlight;
    } finally {
      this.reauthInFlight = null;
    }
  }

  /**
   * Make an API request. On a 401 (expired/invalid token) we silently
   * re-register with a fresh signed initData blob and retry the request once,
   * so the short (1h) token lifetime is invisible to the user. `_isRetry`
   * guards against an infinite re-auth loop.
   */
  private async request<T>(
    endpoint: string,
    options: RequestInit = {},
    _isRetry = false
  ): Promise<ApiResponse<T>> {
    try {
      const response = await fetch(`${API_BASE_URL}${endpoint}`, {
        ...options,
        headers: {
          'Content-Type': 'application/json',
          ...this.getAuthHeaders(),
          ...options.headers,
        },
      });

      if (response.status === 401 && !_isRetry) {
        const reauthed = await this.reauthenticate();
        if (reauthed) return this.request<T>(endpoint, options, true);
      }

      const data = await response.json().catch(() => null);

      if (!response.ok) {
        return {
          success: false,
          error: data?.error || data?.message || `Request failed: ${response.status}`,
        };
      }

      return { success: true, data };
    } catch (error) {
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Network error',
      };
    }
  }

  /**
   * Fetch public worker config (env-specific settings like notification bot username)
   */
  async getConfig(): Promise<{ notificationBotUsername: string | null; supportBotUsername: string | null }> {
    try {
      const response = await fetch(`${API_BASE_URL}/config`);
      if (!response.ok) return { notificationBotUsername: null, supportBotUsername: null };
      const data = await response.json();
      return {
        notificationBotUsername: data.notificationBotUsername ?? null,
        supportBotUsername: data.supportBotUsername ?? null,
      };
    } catch {
      return { notificationBotUsername: null, supportBotUsername: null };
    }
  }

  /**
   * Register → JWT. Always sends the raw signed `initData` blob from the
   * SuperApp bridge when available. The server enforces it on UAT/PROD
   * (verifies the HMAC, trusts only the signed deviceId) and is lenient on dev
   * (a deviceId-only call still works). Returns 200 with a token for an
   * authorized device, 401 otherwise.
   */
  async register(
    deviceId: string,
    initData?: string
  ): Promise<ApiResponse<{ token: string; user: { deviceId: string; profileId: string; username: string; level: number; telegramUserId?: string | null } }>> {
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), 15_000);

    try {
      const response = await fetch(`${API_BASE_URL}/auth/register`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(initData ? { deviceId, initData } : { deviceId }),
        signal: controller.signal,
      });
      clearTimeout(timeoutId);

      const data = await response.json().catch(() => null);

      if (!response.ok) {
        return {
          success: false,
          error: data?.error || data?.message || `Request failed: ${response.status}`,
        };
      }

      if (data?.token) {
        localStorage.setItem(AUTH_TOKEN_KEY, data.token);
      }

      return {
        success: true,
        data: {
          token: data.token,
          user: data.user,
        },
      };
    } catch (error) {
      clearTimeout(timeoutId);
      const message = error instanceof Error ? error.message : 'Network error';
      const isTimeout = error instanceof Error && error.name === 'AbortError';
      return {
        success: false,
        error: isTimeout ? 'Request timed out. Check your connection and that the backend is running.' : message,
      };
    }
  }

  /**
   * Verify session token. Returns `null` when invalid/missing.
   * On success returns the fresh user fields from `/api/auth/verify` —
   * the server re-reads `level` (and the rest) from the DB on every call,
   * so this is the right place to pick up role changes (Phase 34: L1→L2
   * elevations) without forcing a full `/register` round-trip. Callers
   * MUST merge the returned values into their cached user so a stale
   * localStorage copy doesn't keep claiming the old level forever.
   */
  async verifySession(): Promise<{
    profileId: string;
    username: string;
    level: number;
    telegramUserId?: string | null;
  } | null> {
    const token = localStorage.getItem(AUTH_TOKEN_KEY);
    if (!token) return null;

    const result = await this.request<{
      valid: boolean;
      user?: { deviceId: string; profileId: string; username: string; level: number; telegramUserId?: string | null };
    }>('/auth/verify');
    if (!result.success || result.data?.valid !== true || !result.data.user) return null;
    const { profileId, username, level, telegramUserId } = result.data.user;
    return { profileId, username, level, telegramUserId };
  }

  /**
   * Check if technician has linked their Telegram account for personal notifications.
   * Calls GET /api/auth/verify which now returns telegramLinked in response.
   */
  async checkTelegramLinked(): Promise<boolean> {
    const result = await this.request<{ valid: boolean; telegramLinked?: boolean }>('/auth/verify');
    return result.success && result.data?.telegramLinked === true;
  }

  /**
   * Unlink Telegram personal notifications for this device.
   */
  async unlinkTelegramNotify(): Promise<boolean> {
    const result = await this.request<{ ok: boolean }>('/auth/unlink-notify', {
      method: 'POST',
    });
    return result.success && result.data?.ok === true;
  }

  /**
   * Sign out: ask the server to revoke this token, then clear
   * local state. Phase 35 — Pen-test Finding #3.
   *
   * Best-effort over the wire: if the network call fails (server
   * down, no connectivity, even a 500) we STILL clear local
   * state and bounce to the auth entry. The token's 1h TTL caps
   * the blast radius. CONTEXT line 41 explicit requirement.
   *
   * Direct fetch — bypasses this.request() because request()'s
   * 401 retry loop would silent-re-register on 401, defeating
   * the logout. We just need the Authorization header on a
   * one-shot POST.
   */
  async logout(): Promise<void> {
    const token = localStorage.getItem(AUTH_TOKEN_KEY);
    if (token) {
      try {
        await fetch(`${API_BASE_URL}/auth/logout`, {
          method: 'POST',
          headers: { 'Authorization': `Bearer ${token}` },
        });
      } catch {
        /* swallow — best-effort */
      }
    }
    localStorage.removeItem(AUTH_TOKEN_KEY);
  }

  /**
   * Get dashboard statistics.
   *
   * Optional `from`/`to`/`q` apply the same filter as `/tickets/open` so the
   * tab badges in the list view track the filtered counts across all
   * statuses. Omit for the global counts the Dashboard shows.
   */
  async getDashboardStats(
    opts: { from?: string; to?: string; q?: string } = {},
  ): Promise<ApiResponse<DashboardStats>> {
    const params = new URLSearchParams();
    if (opts.from) params.set('from', opts.from);
    if (opts.to) params.set('to', opts.to);
    if (opts.q && opts.q.trim()) params.set('q', opts.q.trim());
    const qs = params.size > 0 ? `?${params.toString()}` : '';
    return this.request<DashboardStats>(`/tickets/stats${qs}`);
  }

  /**
   * Get open tickets (paginated, 100 per page).
   *
   * Options:
   *   - `status` scopes to a single bucket; omit for the unified active set
   *     (used by the Dashboard).
   *   - `page` selects the 1-based page (default 1).
   *   - `sort` picks the ORDER BY (default `unread`).
   *   - `from` / `to` filter `created_at` to a YYYY-MM-DD range (inclusive
   *     of both endpoint days). Invalid date strings are dropped server-side.
   *   - `q` is a case-insensitive substring search across ticket_id, username,
   *     first/last_name, phone_number, initial_message, external_reference_id.
   */
  async getOpenTickets(
    opts: {
      status?: 'open' | 'in_progress' | 'escalated';
      page?: number;
      sort?: 'unread' | 'newest' | 'oldest' | 'activity';
      from?: string;
      to?: string;
      q?: string;
    } = {},
  ): Promise<ApiResponse<TicketsResponse>> {
    const params = new URLSearchParams();
    if (opts.status) params.set('status', opts.status);
    if (opts.page && opts.page > 1) params.set('page', String(opts.page));
    if (opts.sort && opts.sort !== 'unread') params.set('sort', opts.sort);
    if (opts.from) params.set('from', opts.from);
    if (opts.to) params.set('to', opts.to);
    if (opts.q && opts.q.trim()) params.set('q', opts.q.trim());
    const qs = params.size > 0 ? `?${params.toString()}` : '';
    return this.request<TicketsResponse>(`/tickets/open${qs}`);
  }

  /**
   * Get archived tickets with pagination
   */
  async getArchivedTickets(page: number = 1): Promise<ApiResponse<ArchivedTicketsResponse>> {
    return this.request<ArchivedTicketsResponse>(`/tickets/archived?page=${page}`);
  }

  /**
   * Get archived conversations (1 row per user) with pagination and filters.
   *
   * Filters apply against the user's resolved tickets — a user shows up if
   * ≥1 of their tickets matches. The returned `ticket` per row is the most
   * recent matching ticket. `matchedTicket` is non-null only when any filter
   * is active, so the UI can render the "↳ Matched" hint line conditionally.
   */
  async getArchivedConversations(
    opts: {
      page?: number;
      q?: string;
      from?: string;
      to?: string;
      categories?: string[];
      hasFollowup?: boolean;
      sort?: 'most_recent' | 'most_tickets' | 'name_az';
    } = {}
  ): Promise<ApiResponse<ArchivedConversationsResponse>> {
    const params = new URLSearchParams();
    params.set('page', String(opts.page ?? 1));
    if (opts.q && opts.q.trim()) params.set('q', opts.q.trim());
    if (opts.from) params.set('from', opts.from);
    if (opts.to) params.set('to', opts.to);
    if (opts.categories && opts.categories.length > 0) {
      // Server accepts repeated ?category= or comma-list. Comma-list keeps the
      // URL short for the common 1-3 chip case.
      params.set('category', opts.categories.join(','));
    }
    if (opts.hasFollowup === true) params.set('hasFollowup', 'true');
    if (opts.sort) params.set('sort', opts.sort);
    return this.request<ArchivedConversationsResponse>(`/tickets/archived/conversations?${params.toString()}`);
  }

  /**
   * Pin a ticket for the calling technician. Idempotent — pinning a ticket
   * already pinned by this tech returns `alreadyPinned: true` with no
   * state change. Returns 409 when the tech's pin cap is reached.
   */
  async pinTicket(ticketId: string): Promise<ApiResponse<{ success: boolean; sortOrder: number; alreadyPinned: boolean }>> {
    return this.request(`/tickets/${encodeURIComponent(ticketId)}/pin`, { method: 'POST' });
  }

  /** Unpin a ticket for the calling technician. Idempotent. */
  async unpinTicket(ticketId: string): Promise<ApiResponse<{ success: boolean; removed: boolean }>> {
    return this.request(`/tickets/${encodeURIComponent(ticketId)}/pin`, { method: 'DELETE' });
  }

  /**
   * Replace the calling technician's pin order. `ticketIds` must contain
   * exactly the tech's currently-pinned ticket ids in the new display order
   * (top-first). Mismatched lists → 400 with `missingTicketIds` in the body.
   */
  async reorderPins(ticketIds: string[]): Promise<ApiResponse<{ success: boolean }>> {
    return this.request(`/tickets/pins/order`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ ticketIds }),
    });
  }

  /**
   * Get a single ticket with messages.
   *
   * Always hits the network (no TTL cache, no in-flight dedupe): mount +
   * WS onOpen + send/close can overlap; sharing one in-flight request
   * would return metadata from before the latest write (e.g. a post-close
   * refetch would coalesce onto a pre-close request and snap the UI back
   * to "open"). The wasted requests on quick double-fires are cheap; the
   * stale-state UI from dedupe is not.
   */
  async getTicket(ticketId: string): Promise<ApiResponse<TicketResponse>> {
    return this.request<TicketResponse>(`/tickets/${encodeURIComponent(ticketId)}`);
  }

  /**
   * Paginated unified conversation for the ticket's user (scroll-up history).
   */
  async getConversationMessages(
    ticketId: string,
    opts?: { beforeTimestamp?: string; beforeId?: number; limit?: number }
  ): Promise<ApiResponse<ConversationMessagesResponse>> {
    const params = new URLSearchParams();
    params.set('limit', String(opts?.limit ?? 25));
    if (opts?.beforeTimestamp !== undefined && opts?.beforeId !== undefined) {
      params.set('beforeTimestamp', opts.beforeTimestamp);
      params.set('beforeId', String(opts.beforeId));
    }
    const token = localStorage.getItem(AUTH_TOKEN_KEY) || '';
    const cacheKey = `conv:${token}:${ticketId}:${params.toString()}`;
    const path = `/tickets/${encodeURIComponent(ticketId)}/messages?${params.toString()}`;

    // Older pages: dedupe scroll-up fetches. Latest tail: do not dedupe — a second caller may need
    // a fresher tail after a send/notify while an older in-flight GET would return stale rows.
    const isOlderPage =
      opts?.beforeTimestamp !== undefined && opts?.beforeId !== undefined;
    if (isOlderPage) {
      return this.dedupe<ConversationMessagesResponse>(cacheKey, async () => {
        return this.request<ConversationMessagesResponse>(path);
      });
    }

    return this.request<ConversationMessagesResponse>(path);
  }

  /**
   * Mark ticket messages as read
   */
  async markAsRead(ticketId: string): Promise<ApiResponse<SuccessResponse>> {
    return this.request<SuccessResponse>(`/tickets/${ticketId}/read`, {
      method: 'POST',
    });
  }

  /**
   * Send a text reply.
   *
   * Phase 33: when `replyToMessageId` is supplied, the server resolves
   * the DB id → telegram_message_id and threads `replyParameters` onto
   * the outbound queue job so the user sees a Telegram-native quoted
   * reply. When omitted, the JSON body is byte-for-byte identical to
   * pre-Phase-33 — the conditional-spread drops the field entirely
   * rather than sending `null` or `undefined` over the wire.
   */
  async sendReply(
    ticketId: string,
    message: string,
    replyToMessageId?: number,
  ): Promise<ApiResponse<SuccessResponse>> {
    return this.request<SuccessResponse>(`/tickets/${ticketId}/reply`, {
      method: 'POST',
      body: JSON.stringify({
        message,
        ...(replyToMessageId !== undefined ? { replyToMessageId } : {}),
      }),
    });
  }

  /**
   * Edit-message phase — rewrite the text/caption of a single technician
   * message. The server enforces the L1 (author-only) / L2 (any-tech)
   * permission gate plus the 48 h Telegram edit window. Caller should
   * gate the UI so the Edit action is hidden when the row is ineligible.
   */
  async editMessage(
    ticketId: string,
    messageId: number,
    text: string,
  ): Promise<ApiResponse<{ messageId: number; text: string; editedAt: string }>> {
    return this.request(`/tickets/${ticketId}/messages/${messageId}`, {
      method: 'PATCH',
      body: JSON.stringify({ text }),
    });
  }

  /**
   * v4.1: Soft-delete one or more messages on a ticket and unsend them
   * from the user's Telegram chat. Strict policy: every targeted row must
   * be technician-sent + have a captured telegram_message_id + be ≤48 h
   * old. If any row fails this check the server rejects the whole batch
   * with 400 — the caller is expected to gate the UI so ineligible rows
   * can't be selected.
   */
  async deleteMessages(
    ticketId: string,
    messageIds: number[],
  ): Promise<ApiResponse<{
    deletedCount: number;
    deletedFromTelegramCount: number;
    skippedAlreadyDeleted: number;
  }>> {
    return this.request(`/tickets/${ticketId}/messages`, {
      method: 'DELETE',
      body: JSON.stringify({ messageIds }),
    });
  }

  /**
   * Send a reply with attachments
   * Sends text via /reply, then uploads each attachment via /media as FormData.
   *
   * Phase 33: optional `replyToMessageId` is forwarded to the inner
   * `sendReply` call — only the text portion of a reply carries
   * `reply_parameters` today. Attachments uploaded via the /media
   * endpoint land as separate Telegram sends and do NOT carry
   * `reply_parameters`; extending /media to accept replyToMessageId
   * would require a second route extension + outbound queue media-job
   * replyParameters wiring (the infrastructure from plan 33-02 is
   * already in place for media kinds, only the wire-up is deferred).
   * When a reply has only attachments (no text), the quoted-reply UX
   * degrades to plain sends — see 33-05-SUMMARY.md "Known limitation".
   */
  async sendReplyWithAttachments(
    ticketId: string,
    message: string,
    attachments: Attachment[],
    replyToMessageId?: number,
  ): Promise<ApiResponse<SuccessResponse>> {
    // Send text message first (if any)
    if (message.trim()) {
      const textResult = await this.sendReply(ticketId, message, replyToMessageId);
      if (!textResult.success) return textResult;
    }

    // Upload each attachment via /media endpoint
    for (const attachment of attachments) {
      let mimeType: string;
      let defaultName: string;
      if (attachment.type === 'image') {
        mimeType = 'image/jpeg';
        defaultName = `attachment.jpg`;
      } else if (attachment.type === 'audio') {
        mimeType = attachment.mimeType || 'audio/aac';
        defaultName = `voice_${Date.now()}.${attachment.extension || 'aac'}`;
      } else {
        mimeType = 'application/octet-stream';
        defaultName = 'attachment.bin';
      }

      const blob = this.base64ToBlob(attachment.base64, mimeType);
      const file = new File([blob], attachment.fileName || defaultName, { type: blob.type });

      // Forward the recorded length for voice notes so the player shows it
      // without decoding the OGG client-side.
      const result = await this.uploadMedia(ticketId, file, undefined, attachment.duration);
      if (!result.success) return result;
    }

    return { success: true };
  }

  /**
   * Convert base64 string to Blob
   */
  private base64ToBlob(base64: string, mimeType: string): Blob {
    const byteChars = atob(base64);
    const byteArray = new Uint8Array(byteChars.length);
    for (let i = 0; i < byteChars.length; i++) {
      byteArray[i] = byteChars.charCodeAt(i);
    }
    return new Blob([byteArray], { type: mimeType });
  }

  /**
   * Upload media to a ticket
   */
  async uploadMedia(
    ticketId: string,
    file: File,
    caption?: string,
    durationSeconds?: number
  ): Promise<ApiResponse<SuccessResponse>> {
    const formData = new FormData();
    formData.append('file', file);
    if (caption) formData.append('caption', caption);
    // Voice notes: send the recorded length so the server stores it and the
    // player shows it without decoding the OGG (unreliable on iOS WebView).
    if (durationSeconds && durationSeconds > 0) {
      formData.append('durationSeconds', String(Math.round(durationSeconds)));
    }

    try {
      const response = await fetch(`${API_BASE_URL}/tickets/${ticketId}/media`, {
        method: 'POST',
        headers: this.getAuthHeaders(),
        body: formData,
      });

      const data = await response.json().catch(() => null);

      if (!response.ok) {
        return {
          success: false,
          error: data?.error || 'Upload failed',
        };
      }

      return { success: true, data };
    } catch (error) {
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Upload failed',
      };
    }
  }

  /**
   * Upload media from base64
   */
  async uploadMediaBase64(
    ticketId: string,
    attachment: Attachment,
    caption?: string
  ): Promise<ApiResponse<SuccessResponse>> {
    return this.request<SuccessResponse>(`/tickets/${ticketId}/media`, {
      method: 'POST',
      body: JSON.stringify({
        base64: attachment.base64,
        fileName: attachment.fileName,
        fileType: attachment.type,
        caption,
      }),
    });
  }

  /**
   * Close a ticket with service types (multi) + categories (multi) + optional close note
   */
  async closeTicket(
    ticketId: string,
    serviceTypes: ServiceType[],
    categories: string[],
    closeNote?: string
  ): Promise<ApiResponse<SuccessResponse>> {
    return this.request<SuccessResponse>(`/tickets/${ticketId}/close`, {
      method: 'POST',
      body: JSON.stringify({ serviceTypes, categories, closeNote }),
    });
  }

  /**
   * Close N tickets at once with shared close metadata. Used by the
   * multi-select close flow in the user-conversation view. Server caps at
   * 50 IDs per request; already-closed IDs in the list are silently
   * skipped (the response's closedCount reflects what actually changed).
   */
  async closeTicketBatch(
    ticketIds: string[],
    serviceTypes: ServiceType[],
    categories: string[],
    closeNote?: string,
  ): Promise<ApiResponse<{ success: boolean; closedCount: number; closedTicketIds: string[] }>> {
    return this.request<{ success: boolean; closedCount: number; closedTicketIds: string[] }>(
      `/tickets/close-batch`,
      {
        method: 'POST',
        body: JSON.stringify({ ticketIds, serviceTypes, categories, closeNote }),
      },
    );
  }

  /**
   * Phase 34: Level-2 reopen of a closed ticket. Server enforces level>=2
   * via `requireLevel(2)`; this client method does NOT pre-check level so
   * a stale UI never silently swallows a 403 — let the server decide.
   *
   * Returns the updated ticket payload on success so the caller can
   * replace its local state without a follow-up GET. 409 from the server
   * (non-closed) is surfaced as `success: false` with the wire error
   * string — the caller is expected to refresh the ticket and retry only
   * if the ticket really is closed.
   */
  async reopenTicket(
    ticketId: string,
    reason: string,
  ): Promise<ApiResponse<{ success: boolean; ticket: import('../types').Ticket }>> {
    return this.request<{ success: boolean; ticket: import('../types').Ticket }>(
      `/tickets/${encodeURIComponent(ticketId)}/reopen`,
      {
        method: 'POST',
        body: JSON.stringify({ reason }),
      },
    );
  }

  /**
   * The user's currently-active tickets (open / in_progress / escalated).
   * Used to mark which TICK-XXXX dividers in the unified conversation
   * history are still selectable for close. Small list — one user rarely
   * has more than a few active tickets — so no pagination.
   */
  async getUserActiveTickets(userId: number): Promise<ApiResponse<{ tickets: Ticket[] }>> {
    return this.request<{ tickets: Ticket[] }>(`/tickets/user/${userId}/active`);
  }

  /**
   * Get available categories
   */
  async getCategories(): Promise<ApiResponse<{ categories: Category[] }>> {
    return this.request<{ categories: Category[] }>('/tickets/categories');
  }

  /**
   * Get available service types (used by close-ticket modal Step 1)
   */
  async getServiceTypes(): Promise<ApiResponse<{ serviceTypes: ServiceTypeOption[] }>> {
    return this.request<{ serviceTypes: ServiceTypeOption[] }>('/tickets/service-types');
  }

  /**
   * Phase 32-polish: re-fetch SimVerifier case context for a ticket.
   * Returns success:true + updated ticket when the API responded, or
   * success:false + cached ticket when the API was unreachable.
   * Never throws — failures are surfaced via success:false.
   */
  async refreshExternalContext(
    ticketId: string
  ): Promise<ApiResponse<{ success: boolean; ticket: import('../types').Ticket }>> {
    return this.request<{ success: boolean; ticket: import('../types').Ticket }>(
      `/tickets/${encodeURIComponent(ticketId)}/external-refresh`
    );
  }

  /**
   * Phase 32 inline-divider: batch-fetch external metadata for a list of ticket IDs.
   * Calls POST /api/tickets/external-refs — one round-trip for the whole conversation.
   * Tickets with no external metadata are absent from the returned map.
   * Never throws — failures are surfaced via success:false.
   */
  async getTicketsExternal(
    ticketIds: string[]
  ): Promise<ApiResponse<{ tickets: Record<string, ExternalContext> }>> {
    return this.request<{ tickets: Record<string, ExternalContext> }>(
      '/tickets/external-refs',
      {
        method: 'POST',
        body: JSON.stringify({ ids: ticketIds }),
      }
    );
  }

  // ── Phase 37: Canned responses ───────────────────────────────────────────
  // Routes mounted at /api/canned-responses (37-03). GET / is open to any
  // authenticated technician (the picker); every category/answer mutation is
  // requireLevel(2)-gated server-side — this client does NOT pre-check level so
  // a stale UI surfaces the server's 403 rather than silently swallowing it.
  // Request bodies carry free-text `name` / `title` / `body` fields.

  /**
   * Grouped picker list (any authed tech). Returns categories with their
   * nested ACTIVE answers; empty categories come back with `answers: []`.
   */
  async getCannedResponses(): Promise<ApiResponse<PickerCategory[]>> {
    return this.request<PickerCategory[]>('/canned-responses');
  }

  /** Admin: flat category list (requireLevel(2)). */
  async listCannedCategories(): Promise<ApiResponse<CannedCategory[]>> {
    return this.request<CannedCategory[]>('/canned-responses/categories');
  }

  /**
   * Admin: create a category. Phase 38: `body` may carry an optional `caseType`
   * ('SV'|'WL'|null) — it is forwarded verbatim (omitted → server defaults to
   * General/NULL).
   */
  async createCannedCategory(
    body: CreateCategoryInput,
  ): Promise<ApiResponse<CannedCategory>> {
    return this.request<CannedCategory>('/canned-responses/categories', {
      method: 'POST',
      // Forward caseType verbatim when present (including the input's other
      // fields); null is a meaningful value (clear-to-General) so it is sent
      // explicitly rather than dropped.
      body: JSON.stringify({
        ...body,
        ...(body.caseType !== undefined ? { caseType: body.caseType } : {}),
      }),
    });
  }

  /**
   * Admin: partial-update a category. Phase 38: when `body.caseType` is present
   * it is forwarded — `'SV'`/`'WL'` sets the tag, explicit `null` CLEARS it back
   * to General (key-presence drives the server SET); omitting the key leaves the
   * tag untouched.
   */
  async updateCannedCategory(
    id: number,
    body: UpdateCategoryInput,
  ): Promise<ApiResponse<CannedCategory>> {
    return this.request<CannedCategory>(`/canned-responses/categories/${id}`, {
      method: 'PATCH',
      body: JSON.stringify({
        ...body,
        ...(body.caseType !== undefined ? { caseType: body.caseType } : {}),
      }),
    });
  }

  /** Admin: delete a category (409 if it still has answers). */
  async deleteCannedCategory(
    id: number,
  ): Promise<ApiResponse<{ success: boolean }>> {
    return this.request<{ success: boolean }>(`/canned-responses/categories/${id}`, {
      method: 'DELETE',
    });
  }

  /** Admin: flat answer list INCLUDING inactive (requireLevel(2)). */
  async listCannedAnswers(): Promise<ApiResponse<CannedAnswer[]>> {
    return this.request<CannedAnswer[]>('/canned-responses/answers');
  }

  /**
   * Admin: create an answer. `createdBy` is set server-side from the JWT.
   * A duplicate (categoryId, title) → 409.
   */
  async createCannedAnswer(
    body: CreateAnswerInput,
  ): Promise<ApiResponse<CannedAnswer>> {
    return this.request<CannedAnswer>('/canned-responses/answers', {
      method: 'POST',
      body: JSON.stringify(body),
    });
  }

  /** Admin: partial-update an answer (duplicate title → 409). */
  async updateCannedAnswer(
    id: number,
    body: UpdateAnswerInput,
  ): Promise<ApiResponse<CannedAnswer>> {
    return this.request<CannedAnswer>(`/canned-responses/answers/${id}`, {
      method: 'PATCH',
      body: JSON.stringify(body),
    });
  }

  /**
   * Admin: HARD-delete an answer (permanent — distinct from the is_active
   * soft-delete toggle). 404 if it no longer exists. Mirrors
   * deleteCannedCategory.
   */
  async deleteCannedAnswer(id: number): Promise<ApiResponse<{ ok: boolean }>> {
    return this.request<{ ok: boolean }>(`/canned-responses/answers/${id}`, {
      method: 'DELETE',
    });
  }

  /** Admin: soft-delete / restore toggle via the is_active flag. */
  async setCannedAnswerActive(
    id: number,
    isActive: boolean,
  ): Promise<ApiResponse<CannedAnswer>> {
    return this.request<CannedAnswer>(`/canned-responses/answers/${id}/active`, {
      method: 'PATCH',
      body: JSON.stringify({ isActive }),
    });
  }

  // ── Phase 37 / Plan 06+07: CSV bulk import ────────────────────────────────
  // POST /api/canned-responses/import?mode=validate|commit (37-06): multipart,
  // field "file", requireLevel(2)-gated. validate = dry-run (always 200, writes
  // nothing); commit = atomic upsert (422 on any row error, writes nothing).
  // The import is an UPSERT keyed on (category + title): re-upload updates
  // existing answers + adds new ones, NEVER deletes out-of-file answers.

  /**
   * Validate (dry-run) a CSV: returns the per-row preview (each row tagged
   * New / Update / will-create-category / error) + the header/row errors.
   * Writes NOTHING server-side. Always resolves with the validate envelope.
   *
   * The file is sent as multipart `FormData` (field "file") — the browser sets
   * the multipart boundary, so we must NOT hand-set a Content-Type. The Bearer
   * token is preserved via `importFormData` (same auth source as `request`,
   * with the same single 401-retry).
   */
  async validateCannedImport(
    file: File,
    replace = false,
  ): Promise<ApiResponse<ImportValidateResult>> {
    return this.importFormData<ImportValidateResult>(file, 'validate', replace);
  }

  /**
   * Commit a CSV (atomic upsert). On a clean file → 200 with the summary
   * (created / updated / reactivated counts + created categories). On any row
   * error → 422 with `ok:false` + the per-row errors (whole-file reject, no
   * writes). The caller must only commit a file whose validate pass had zero
   * errors (the UI disables Confirm otherwise).
   */
  async commitCannedImport(
    file: File,
    replace = false,
  ): Promise<ApiResponse<ImportCommitResult>> {
    return this.importFormData<ImportCommitResult>(file, 'commit', replace);
  }

  /**
   * Unified two-step import client used by the hook. `mode` selects the
   * validate (dry-run) or commit (upsert) pass; both POST the same multipart
   * body to the single `/canned-responses/import` endpoint with `?mode=`.
   */
  async importCannedResponses(
    file: File,
    mode: 'validate' | 'commit',
    replace = false,
  ): Promise<ApiResponse<ImportValidateResult | ImportCommitResult>> {
    return this.importFormData<ImportValidateResult | ImportCommitResult>(file, mode, replace);
  }

  /**
   * Internal: POST the CSV as multipart FormData to the import endpoint,
   * preserving the Bearer token and retrying once on a 401 (mirrors `request`).
   * Note: a 422 (commit reject with row errors) is a "successful" response for
   * our purposes — the body carries `ok:false` + errors — so we treat the
   * parsed JSON body as `data` even on a non-2xx status WHEN the body has the
   * expected `{ ok, mode }` shape. A 401/403/500 with no such body becomes an
   * error string.
   */
  private async importFormData<T>(
    file: File,
    mode: 'validate' | 'commit',
    replace = false,
    _isRetry = false,
  ): Promise<ApiResponse<T>> {
    const fd = new FormData();
    fd.append('file', file);
    try {
      const response = await fetch(
        `${API_BASE_URL}/canned-responses/import?mode=${mode}&replace=${replace ? 'true' : 'false'}`,
        {
          method: 'POST',
          // getAuthHeaders() only sets Authorization — NO Content-Type, so the
          // browser sets the multipart boundary itself.
          headers: this.getAuthHeaders(),
          body: fd,
        },
      );

      if (response.status === 401 && !_isRetry) {
        const reauthed = await this.reauthenticate();
        if (reauthed) return this.importFormData<T>(file, mode, replace, true);
      }

      const data = await response.json().catch(() => null);

      // A 422 commit-reject carries the validate-style envelope (ok:false +
      // errors/preview). Surface that as DATA so the UI can render the row
      // errors rather than a bare error banner.
      const looksLikeImportEnvelope =
        data && typeof data === 'object' && 'ok' in data && 'mode' in data;

      if (!response.ok && !looksLikeImportEnvelope) {
        return {
          success: false,
          error: data?.error || data?.message || `Import failed: ${response.status}`,
        };
      }

      return { success: true, data: data as T };
    } catch (error) {
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Import failed',
      };
    }
  }

  /**
   * Download the CSV template (header + the seeded sample row) so admins fill
   * the exact shape/encoding. The route is requireLevel(2)-gated
   * (37-06), so we fetch it WITH the Bearer token and trigger a client-side
   * download (Blob → object URL → temporary anchor) — opening the URL directly
   * would drop the auth header and 401. Filename:
   * `canned-responses-template.csv`. Returns ok:false (no throw) on failure so
   * the caller can show an inline error.
   */
  async downloadCannedTemplate(): Promise<{ ok: true } | { ok: false; error: string }> {
    try {
      const response = await fetch(
        `${API_BASE_URL}/canned-responses/import/template`,
        { headers: this.getAuthHeaders() },
      );
      if (!response.ok) {
        return { ok: false, error: `Template download failed: ${response.status}` };
      }
      const buf = await response.arrayBuffer();
      const fileName = 'canned-responses-template.csv';

      // Inside the SuperApp, SAVE the file straight to the device. We do NOT
      // use the share sheet here: the SuperApp's iOS share sheet has a known
      // bug (sharePositionOrigin) that falls back to an inline CSV view (a tiny
      // unreadable table). saveResource writes the file directly with no inline
      // view. Dynamic import avoids the bridge↔api cycle.
      try {
        const { bridgeService } = await import('./bridge');
        if (bridgeService.isInSuperApp) {
          // base64-encode the UTF-8 CSV bytes (Khmer-safe — encode raw bytes).
          const bytes = new Uint8Array(buf);
          let bin = '';
          for (let i = 0; i < bytes.length; i++) bin += String.fromCharCode(bytes[i]);
          const base64 = btoa(bin);
          const saved = await bridgeService.saveFile(
            base64,
            'canned-responses-template',
            'csv',
            'anyFile',
          );
          if (saved) return { ok: true };
          // save failed → fall through to the browser-download path
        }
      } catch {
        // bridge unavailable → fall through to the browser-download path
      }

      // Web / fallback: trigger a normal browser download.
      const blob = new Blob([buf], { type: 'text/csv;charset=utf-8' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = fileName;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      return { ok: true };
    } catch (error) {
      return {
        ok: false,
        error: error instanceof Error ? error.message : 'Template download failed',
      };
    }
  }
  // ── Phase 39: Announcements / Broadcast API methods ──────────────────────
  // All endpoints require JWT + level>=2 (requireLevel(2) server-side).

  /** List all broadcast campaigns (newest first). */
  async listBroadcasts(): Promise<ApiResponse<Broadcast[]>> {
    return this.request<Broadcast[]>('/broadcasts');
  }

  /** Get a single broadcast with full detail + pending_count. */
  async getBroadcast(id: string): Promise<ApiResponse<BroadcastDetail>> {
    return this.request<BroadcastDetail>(`/broadcasts/${id}`);
  }

  /**
   * Preview the audience size for the given filter without creating a broadcast.
   * Returns { count } — the number of distinct user_ids matched.
   */
  async previewBroadcastAudience(filter: AudienceFilter): Promise<ApiResponse<{ count: number }>> {
    return this.request<{ count: number }>('/broadcasts/preview', {
      method: 'POST',
      body: JSON.stringify({ audience_filter: filter }),
    });
  }

  /**
   * Create a broadcast: snapshot recipients, set status=sending, background-dispatch.
   * Returns 202 { id, status, totalRecipients }.
   * A 409 means confirm_count doesn't match a freshly-computed audience count.
   */
  async createBroadcast(
    body: CreateBroadcastInput,
  ): Promise<ApiResponse<{ id: string; status: string; totalRecipients: number }>> {
    return this.request('/broadcasts', {
      method: 'POST',
      body: JSON.stringify(body),
    });
  }

  /** Cancel an in-flight broadcast (status must be 'sending'). */
  async cancelBroadcast(id: string): Promise<ApiResponse<{ ok: boolean }>> {
    return this.request<{ ok: boolean }>(`/broadcasts/${id}/cancel`, { method: 'POST' });
  }

  /** Re-enqueue failed (not blocked) recipients for a broadcast. */
  async retryFailedBroadcast(id: string): Promise<ApiResponse<{ ok: boolean; requeued: number }>> {
    return this.request<{ ok: boolean; requeued: number }>(`/broadcasts/${id}/retry-failed`, {
      method: 'POST',
    });
  }

  /**
   * Resume a stuck 'sending' broadcast by re-dispatching its pending recipients.
   * Returns 202 { ok: true, requeued: N } where N is the count of pending rows
   * that were enqueued. The worker idempotency guard ensures no double-sends.
   * Returns 409 if the broadcast is not in 'sending' status.
   */
  async resumeBroadcast(id: string): Promise<ApiResponse<{ ok: boolean; requeued: number }>> {
    return this.request<{ ok: boolean; requeued: number }>(`/broadcasts/${id}/resume`, {
      method: 'POST',
    });
  }

  /**
   * Send a test message to a specific chat ID (D-02).
   * Carries NO broadcastRecipientId — never mutates broadcast state.
   */
  async testSendBroadcast(
    chatId: number | string,
    message: string,
    opts?: { title?: string; linkUrl?: string; linkLabel?: string },
  ): Promise<ApiResponse<{ ok: boolean }>> {
    return this.request<{ ok: boolean }>('/broadcasts/test-send', {
      method: 'POST',
      body: JSON.stringify({
        chat_id: chatId,
        message,
        title: opts?.title,
        link_url: opts?.linkUrl,
        link_label: opts?.linkLabel,
      }),
    });
  }

  /**
   * List distinct category tags actually present in tickets (D-03).
   * Used to populate the category filter picker in the compose form.
   */
  async listBroadcastCategoryTags(): Promise<ApiResponse<{ tags: string[] }>> {
    return this.request<{ tags: string[] }>('/broadcasts/category-tags');
  }

  /**
   * Mint a short-lived public CSV download link for a broadcast.
   *
   * Calls GET /broadcasts/:id/export-link (L2-gated) which returns a
   * signed URL valid for 1 hour. The signed URL (/api/broadcasts/dl/:token)
   * requires no auth header, so the DG SuperApp bridge can fetch it directly
   * via shareFile (native save/share) or window.open can trigger a browser
   * Content-Disposition download.
   */
  async getBroadcastCsvLink(id: string): Promise<ApiResponse<{ url: string }>> {
    return this.request<{ url: string }>(`/broadcasts/${id}/export-link`);
  }

  /**
   * Fetch the recipients CSV as raw text via the authenticated L2 endpoint
   * (same request mechanism as every other API call). Used by the SuperApp
   * Save path — we hand these bytes to the native saveFile so the device
   * writes the real CSV (fetching the public signed URL from the webview was
   * unreliable there).
   */
  async getBroadcastCsvText(id: string): Promise<{ ok: boolean; text?: string; error?: string }> {
    try {
      const res = await fetch(`${API_BASE_URL}/broadcasts/${id}/export.csv`, {
        headers: this.getAuthHeaders(),
      });
      if (!res.ok) return { ok: false, error: `Request failed: ${res.status}` };
      return { ok: true, text: await res.text() };
    } catch (e) {
      return { ok: false, error: e instanceof Error ? e.message : 'Network error' };
    }
  }

  /**
   * Fetch the INVALID-recipients CSV as raw text via the authenticated L2
   * endpoint — the reliable-in-webview sibling of the public /dl/:token
   * (kind:'invalid') download. Same mechanism as getBroadcastCsvText.
   */
  async getInvalidCsvText(id: string): Promise<{ ok: boolean; text?: string; error?: string }> {
    try {
      const res = await fetch(`${API_BASE_URL}/broadcasts/${id}/invalid.csv`, {
        headers: this.getAuthHeaders(),
      });
      if (!res.ok) return { ok: false, error: `Request failed: ${res.status}` };
      return { ok: true, text: await res.text() };
    } catch (e) {
      return { ok: false, error: e instanceof Error ? e.message : 'Network error' };
    }
  }

  /**
   * Download the recipients CSV for a broadcast.
   *
   * @deprecated Use getBroadcastCsvLink + bridgeService.shareFile (SuperApp)
   * or window.open (browser) instead — the blob+<a download> approach renders
   * the CSV inline in the iOS WebView rather than triggering a native save.
   * Kept for reference; call site in AnnouncementsScreen.tsx has been updated.
   *
   * Fetches GET /broadcasts/:id/export.csv with the Bearer auth header,
   * receives the response as a Blob, creates an object URL, triggers a
   * browser download via a temporary <a download> element, then revokes
   * the object URL. Returns { success: true } on success, or
   * { success: false, error } if the request fails.
   */
  async downloadBroadcastCsv(id: string): Promise<{ success: true } | { success: false; error: string }> {
    try {
      const token = localStorage.getItem(AUTH_TOKEN_KEY);
      const response = await fetch(`${API_BASE_URL}/broadcasts/${id}/export.csv`, {
        headers: token ? { Authorization: `Bearer ${token}` } : {},
      });
      if (!response.ok) {
        const text = await response.text().catch(() => '');
        return { success: false, error: `HTTP ${response.status}: ${text}` };
      }
      const blob = await response.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `broadcast-${id}.csv`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
      return { success: true };
    } catch (err) {
      return {
        success: false,
        error: err instanceof Error ? err.message : 'Download failed',
      };
    }
  }

  // ── Phase 40: SV sub-targeting + CSV-upload audience + invalid download ────
  // All endpoints require JWT + level>=2 (requireLevel(2) server-side).

  /**
   * List the DISTINCT SIM Verifier sub-category codes discovered in the data
   * (e.g. ['gf','pvr']; [] when only canonical sv_<uuid> refs exist). Data-driven
   * — the picker renders one chip per code, NEVER a hardcoded list (D-01).
   */
  async listSimVerifierSubs(): Promise<ApiResponse<{ subs: string[] }>> {
    return this.request<{ subs: string[] }>('/broadcasts/sim-verifier-subs');
  }

  /**
   * DRY-RUN a recipient CSV upload (creates NOTHING). Multipart POST with the
   * file under field `file`. Returns the matched/invalid/malformed summary so
   * the admin sees the audience before confirming. Uses raw fetch (the shared
   * `request` wrapper force-sets Content-Type: application/json, which would
   * break the multipart boundary) — mirrors `importFormData`. One 401-retry.
   */
  async uploadRecipients(file: File, _isRetry = false): Promise<ApiResponse<UploadSummary>> {
    const fd = new FormData();
    fd.append('file', file);
    try {
      const response = await fetch(`${API_BASE_URL}/broadcasts/upload`, {
        method: 'POST',
        // getAuthHeaders() only sets Authorization — NO Content-Type, so the
        // browser sets the multipart boundary itself.
        headers: this.getAuthHeaders(),
        body: fd,
      });
      if (response.status === 401 && !_isRetry) {
        const reauthed = await this.reauthenticate();
        if (reauthed) return this.uploadRecipients(file, true);
      }
      const data = await response.json().catch(() => null);
      if (!response.ok) {
        return {
          success: false,
          error: data?.error || data?.message || `Upload failed: ${response.status}`,
        };
      }
      return { success: true, data: data as UploadSummary };
    } catch (error) {
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Upload failed',
      };
    }
  }

  /**
   * Create a broadcast from an uploaded CSV (multipart: title, message,
   * link_url?, link_label?, file). The server re-runs the identical parse+resolve
   * pipeline as the dry-run, snapshots recipients, and background-dispatches.
   * Returns 202 { id, status, totalRecipients, matchedCount, invalidCount }.
   * Same raw-fetch + one-retry pattern as `uploadRecipients` (multipart body).
   */
  async createBroadcastFromUpload(
    fields: { title: string; message: string; link_url?: string | null; link_label?: string | null },
    file: File,
    _isRetry = false,
  ): Promise<ApiResponse<CreateFromUploadResult>> {
    const fd = new FormData();
    fd.append('title', fields.title);
    fd.append('message', fields.message);
    if (fields.link_url) fd.append('link_url', fields.link_url);
    if (fields.link_label) fd.append('link_label', fields.link_label);
    fd.append('file', file);
    try {
      const response = await fetch(`${API_BASE_URL}/broadcasts/upload/create`, {
        method: 'POST',
        headers: this.getAuthHeaders(),
        body: fd,
      });
      if (response.status === 401 && !_isRetry) {
        const reauthed = await this.reauthenticate();
        if (reauthed) return this.createBroadcastFromUpload(fields, file, true);
      }
      const data = await response.json().catch(() => null);
      if (!response.ok) {
        return {
          success: false,
          error: data?.error || data?.message || `Create failed: ${response.status}`,
        };
      }
      return { success: true, data: data as CreateFromUploadResult };
    } catch (error) {
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Create failed',
      };
    }
  }

  /**
   * Mint a short-lived signed URL to download the INVALID-recipients CSV for a
   * broadcast (D-03). The returned /api/broadcasts/dl/:token URL needs no auth
   * header (the kind:'invalid' token is the authorization) — same shape as
   * getBroadcastCsvLink, reused by the report card's "Download invalid" action.
   */
  async getBroadcastInvalidLink(id: string): Promise<ApiResponse<{ url: string }>> {
    return this.request<{ url: string }>(`/broadcasts/${id}/invalid-link`);
  }

  /**
   * Download the CSV recipient-upload template (D-05): headers + one example row
   * + an instruction note. The route is requireLevel(2)-gated, so we fetch WITH
   * the Bearer token and trigger a client-side save (SuperApp bridge saveFile,
   * else a browser Blob download) — opening the URL directly would drop the auth
   * header and 401. Mirrors downloadCannedTemplate.
   */
  async downloadRecipientTemplate(): Promise<{ ok: true } | { ok: false; error: string }> {
    try {
      const response = await fetch(`${API_BASE_URL}/broadcasts/upload/template`, {
        headers: this.getAuthHeaders(),
      });
      if (!response.ok) {
        return { ok: false, error: `Template download failed: ${response.status}` };
      }
      const buf = await response.arrayBuffer();
      const fileName = 'broadcast-recipients-template.csv';
      try {
        const { bridgeService } = await import('./bridge');
        if (bridgeService.isInSuperApp) {
          const bytes = new Uint8Array(buf);
          let bin = '';
          for (let i = 0; i < bytes.length; i++) bin += String.fromCharCode(bytes[i]);
          const base64 = btoa(bin);
          const saved = await bridgeService.saveFile(
            base64,
            'broadcast-recipients-template',
            'csv',
            'anyFile',
          );
          if (saved) return { ok: true };
          // save failed → fall through to the browser-download path
        }
      } catch {
        // bridge unavailable → fall through to the browser-download path
      }
      const blob = new Blob([buf], { type: 'text/csv;charset=utf-8' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = fileName;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      return { ok: true };
    } catch (error) {
      return {
        ok: false,
        error: error instanceof Error ? error.message : 'Template download failed',
      };
    }
  }
}

// Export singleton instance
export const apiClient = new APIClient();
